import * as React from "react";
import { Box } from "@mui/material";
import toast from "react-hot-toast";
import { DownloadPopover } from "../../../../components/DownloadMenu";
import { fetchFileComments, usePublicShare } from "../../../../components/PublicShare/PublicShareContext";
import { CommentsMenu } from "../../../../components/CommentsMenu";
import {
  CommentsFormat,
  downloadVideoComments,
} from "../../../../utils/commentsExport";
import { getReviewComments } from "../../../../utils/commentsPdfGenerator";
import {
  DownloadType,
  formatName,
  processSingleVideoDownload,
} from "./helpers";
import { getRenamedDownload } from "../../../../utils/downloadHelpers";
import { DownloadVideoModal } from "../../../../components/MenuOptions";
import { IVideo } from "../hooks/useFetchVideos";
import { DotLottleLoader } from "../../../../components/Loader";
import { useDownloadGate } from "./useDownloadGate";

interface Props {
  video: IVideo;
  small?: boolean;
  /** New card system (videoSection): 30×30 bordered, tokened trigger. */
  variant?: "default" | "card" | "secondary";
  project?: any;
  /** Skip brand file-type settings entirely (e.g. shared video shortlists). */
  ignoreFileTypeSettings?: boolean;
  /**
   * Brand file-type allowlist already resolved by the caller (public pages
   * carry the brand on the order, not on the video/project). `undefined`
   * falls back to deriving it from video.brandOwners / project.brands.
   */
  allowedFileTypes?: Set<string> | null;
  /**
   * Let the viewer download a not-yet-approved cut. Used by the review
   * surfaces, where the viewer is the reviewer of exactly this version. The
   * usual pre-approval rule still applies: external viewers get the
   * watermarked file only unless the brand enables pre_approval_downloading.
   */
  allowUnapproved?: boolean;
  disabled?: boolean;
}

export function DownloadBtn({
  video,
  small = false,
  variant = "default",
  project,
  ignoreFileTypeSettings = false,
  allowedFileTypes,
  allowUnapproved = false,
  disabled = false,
}: Props) {
  // On a share page the thread comes through the share link (same thread).
  const publicShare = usePublicShare();
  const commentsProject = project ?? (video as any)?.project;
  const handlePickComments = async (format: CommentsFormat) => {
    setShowOptions(false);
    const toastId = toast.loading("Preparing comments...");
    try {
      const data = await fetchFileComments(publicShare, commentsProject?.id, video.id);
      const comments = getReviewComments(data, { includeDeleted: true });
      const sent = await downloadVideoComments(
        { videoName: formatName(video), comments },
        format,
        commentsProject?.title,
        commentsProject?.product?.title,
      );
      if (!sent) {
        toast.error("This video has no comments to download.", { id: toastId });
        return;
      }
      toast.success("Comments downloaded.", { id: toastId });
    } catch (err: any) {
      toast.error(err?.response?.data?.error ?? "Failed to download comments.", { id: toastId });
    }
  };

  // Permission logic lives in useDownloadGate so the panel-level "download all"
  // actions answer to the exact same rule. Behaviour here is unchanged.
  const { isInternalUser, canDownloadUnapproved, canDownload, showOnlyWatermarked, allowedTypes } =
    useDownloadGate(video, project, ignoreFileTypeSettings, { allowedFileTypes, allowUnapproved });
  const brandEnabledFileTypes = allowedTypes;
  const [showOptions, setShowOptions] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [downloadLink, setDownloadLink] = React.useState("");
  const [downloadFileName, setDownloadFileName] = React.useState("");
  const [zippingInProgress, setZippingInProgress] = React.useState(false);

  React.useEffect(() => {
    if (downloadLink !== "") {
      setModalOpen(true);
    }
  }, [downloadLink]);

  const handleClick = async (type: DownloadType) => {
    if (
      !isInternalUser &&
      typeof video.final === "boolean" &&
      !video.final &&
      !canDownloadUnapproved &&
      !project?.download_page &&
      !allowUnapproved
    ) {
      toast.error(
        "This video is not approved yet. Please wait for approval before downloading.",
      );
      return;
    }

    setZippingInProgress(true);
    setDownloadLink("");
    setDownloadFileName("");
    setModalOpen(false);

    try {
      const loadingId =
        type === DownloadType.ALL
          ? toast.loading("Zipping all files, please wait...")
          : null;

      const result = await processSingleVideoDownload(
        video,
        type,
        `${formatName(video)}.zip`,
        false,
        false,
        brandEnabledFileTypes,
      );

      if (loadingId) toast.dismiss(loadingId);

      if (result.error) {
        toast.error(result.error);
        return;
      }

      // Single-file results carry {bucket, key} → presign for a forced filename;
      // zip results have neither and fall back to their source URL as-is.
      const { link, fileName } = await getRenamedDownload({
        bucket: result.bucket,
        key: result.key,
        fileName: result.fileName || "",
        fallbackUrl: result.source,
      });
      if (link) {
        setDownloadFileName(fileName);
        setDownloadLink(link);
      }
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setZippingInProgress(false);
    }
  };

  const isVideoApproved = canDownload;
  // Same three triggers as before: the 30×30 bordered card button, the small
  // icon button, and the full "Download" button.
  let triggerVariant: "primary" | "secondary" | "icon" | "card" = "primary";
  if (variant === "card") triggerVariant = "card";
  else if (variant === "secondary") triggerVariant = "secondary";
  else if (small) triggerVariant = "icon";

  return (
    <>
      {zippingInProgress && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "color-mix(in srgb, var(--surface) 80%, transparent)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1500,
          }}
        >
          <DotLottleLoader fill={false} sx={{ width: 400, height: 400 }} />
        </Box>
      )}

      {/* Exports span every version, so gate on the version-wide count (an
          older API sends only comment_count). */}
      {!!commentsProject?.id && ((video as any)?.total_comment_count ?? (video as any)?.comment_count) !== 0 && (
        <CommentsMenu variant={triggerVariant} onSelect={handlePickComments} />
      )}

      <DownloadPopover
        variant={triggerVariant}
        open={showOptions && !zippingInProgress}
        onToggle={() => setShowOptions((v) => !v)}
        onClose={() => setShowOptions(false)}
        onPick={(key) => {
          setShowOptions(false);
          handleClick(key);
        }}
        disabled={disabled || !isVideoApproved || zippingInProgress}
        label={zippingInProgress ? "Downloading..." : "Download"}
        canDownload={!disabled && isVideoApproved}
        showOnlyWatermarked={showOnlyWatermarked}
        allowedTypes={brandEnabledFileTypes}
      />

      <DownloadVideoModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setDownloadLink("");
          setDownloadFileName("");
        }}
        downloadSource={downloadLink}
        fileName={downloadFileName}
      />
    </>
  );
}
